import { differenceInMinutes, format } from "date-fns";
import parsePhoneNumber from "libphonenumber-js";

import {
  type ResumeContactMatchResponse,
  type CandidateRequest,
  type ResumeIndividualResponse,
  type ResumeListResponse,
  type ResumeRequest,
  type ResumeSimilarityMatchResponse,
  type ResumeWithMatchesResponse,
} from "../api";
import { Utility } from "./utility";

// The Resume model is shared between list and individual endpoints; treat
// every endpoint-specific field as optional.
type ResumeApiResponse = ResumeListResponse &
  Partial<ResumeIndividualResponse> &
  Partial<ResumeWithMatchesResponse>;

export interface Resume extends ResumeApiResponse {}

export class Resume extends Utility<Resume> {
  constructor(init?: Partial<ResumeApiResponse>) {
    super();
    Object.assign(this, init);
  }

  get local_created_at(): Date {
    return this.get_local_time(this.created_at);
  }

  get local_created_at_date_string(): string {
    return format(this.local_created_at, "MMM d, hh:mm aaa");
  }

  get display_name(): string {
    const firstName = this.first_name?.trim();
    const lastName = this.last_name?.trim();
    if (!firstName && !lastName) return this.file_name;
    return [firstName, lastName].filter(Boolean).join(" ");
  }

  get expired(): boolean {
    return false;
    // Temprarily show all resumes
    // return differenceInDays(new Date(), this.local_created_at) > 30;
  }

  get local_evaluation(): string {
    if (this.file_hash === null) return "No evaluation";
    if (this.evaluation?.toLowerCase() === "no evaluation") {
      if (differenceInMinutes(new Date(), this.local_created_at) < 5)
        return "Processing";
      else return "Error";
    }
    return this.evaluation || "Error";
  }

  get local_evaluation_system(): string | null {
    if (this.local_evaluation === null) return null;
    return this.local_evaluation.toLowerCase().trim().replace(" ", "-");
  }

  get local_evaluation_display_text(): string {
    switch (this.local_evaluation_system) {
      case "high-potential":
      case "moderate-potential":
        return "No Disqualification";
      case "low-potential":
        return "Needs Verification";
      case "not-suitable":
        return "Not Suitable";
      case "cannot-process":
        return "Needs Manual Review";
      case "no-evaluation":
        return "No Resume";
      case "error":
        return "Resume Load Failed";
      case "processing":
        return "Processing";
      default:
        return "Unavailable";
    }
  }

  get local_evaluation_score(): number {
    const evaluationScores: {
      [key in NonNullable<Resume["local_evaluation_system"]>]: number;
    } = {
      "not-suitable": 1,
      "low-potential": 2,
      "moderate-potential": 3,
      "high-potential": 4,
    };
    const evaluation = this.local_evaluation_system;
    return evaluation !== null ? (evaluationScores[evaluation] ?? -1) : -1;
  }

  get formatted_phone_number(): string | null {
    if (this.phone_number === null) return null;
    const phoneNumber = parsePhoneNumber(this.phone_number, "US");
    if (phoneNumber === undefined || !phoneNumber.isValid()) return null;
    return phoneNumber.number;
  }

  get phone_available(): boolean {
    return this.formatted_phone_number !== null;
  }

  get ready_to_invite(): boolean {
    return Boolean(this.first_name || this.last_name) && this.phone_available;
  }

  get applying_positions_count(): number {
    return this.applying_positions?.length ?? 0;
  }

  get applying_positions_popover_message(): string {
    if (this.applying_positions && this.applying_positions.length > 0) {
      const positions = this.applying_positions;
      const leadText = `${this.applying_positions_count} applications:\n`;
      return `${leadText} ${positions.map((position, i) => `"${position}"${i === positions.length - 1 ? "" : ","} ${i % 2 === 1 && positions.length > 2 ? "\n" : ""}`).join(" ")}`;
    }
    return "";
  }

  get resumeMatchCount(): number | undefined {
    if ("resume_match_count" in this && this.resume_match_count !== undefined) {
      return this.resume_match_count;
    }
    if ("contact_matches" in this && this.contact_matches !== undefined) {
      return this.contact_matches.length;
    }
    return undefined;
  }

  get contactMatchesDisplay(): (ResumeContactMatchResponse & {
    local_resume_created_at_date_string: string;
  })[] {
    if ("contact_matches" in this && Array.isArray(this.contact_matches)) {
      return this.contact_matches.map((match) => ({
        ...match,
        local_resume_created_at_date_string: format(
          this.get_local_time(match.resume_created_at),
          "MMM d, yyyy",
        ),
      }));
    }
    return [];
  }

  // similarity_matches is getResume-only (ResumeWithMatchesResponse); a list-sourced Resume simply lacks the key.
  // Treat that the same as "none" rather than throwing (0 matches means the similarity tab does not render).
  get similarityMatchesDisplay(): ResumeSimilarityMatchResponse[] {
    if (
      "similarity_matches" in this &&
      Array.isArray(this.similarity_matches)
    ) {
      return this.similarity_matches;
    }
    return [];
  }

  query(query: string): boolean {
    return (
      this.display_name.toLowerCase().includes(query.toLowerCase()) || false
    );
  }

  toRequest(): ResumeRequest {
    return {
      first_name: this.first_name ?? null,
      last_name: this.last_name ?? null,
      phone_number: this.phone_number ?? null,
      archived: this.archived ?? null,
    };
  }

  toCandidateRequest(sms_message: string): CandidateRequest {
    if (!this.ready_to_invite) {
      throw new Error("The specified resume lacks required information");
    }
    return {
      resume_id: this.id,
      first_name: this.first_name ?? "", // Either of first_name or last_name is confirmed to be non-null if ready_to_invite is true
      last_name: this.last_name ?? "",
      phone_number: this.formatted_phone_number!, // This is guaranteed to be non-null if ready_to_invite is true
      sms_message: sms_message,
    };
  }
}

// Trigger lint
